import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';

//Material UI
import { makeStyles } from '@material-ui/core/styles';

//Contexts
import { store as mainStore } from 'store';

//APIs
import axios from 'api/axios';

//Packages
import moment from 'moment-jalaali';

//Hooks
import useRules from 'hooks/useRules';
import { useSearchParams } from 'hooks/useSearchParams';

//Utils
import { __ } from 'utils/i18n';
import { localText } from 'langs/fa/data-grid';
import { formatNumber } from 'fns';

//Components
import DataGrid from 'components/DataGrid/DataGrid';
import Avatar from 'components/User/UserAvatar';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: 480,
    '& .pending': {
      background: '#ffecb3',
    },
    '& .tendering': {
      background: '#e6ee9c',
    },
    '& .getWaybill': {
      background: '#bbdefb',
    },
    '& .rejected': {
      background: '#e0e0e0',
    },
    '& .loadTime': {
      fontSize: 12,
    },
    '& .colHeader': {
      background: '#607d8b',
      color: '#fff',
    },
  },
  fleetCount: {
    '& > span.red': {
      background: '#dd2c00',
      color: '#fff',
      padding: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    '& > span.green': {
      background: '#76ff03',
      padding: theme.spacing(1),
    },
  },
  allOrders: {
    position: 'absolute',
    bottom: 15,
    left: 150,
  },
}));

const OrderListInterestesDrivers = ({
  fullScreen,
  destinationStates,
  driverId,
}) => {
  const classes = useStyles();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const history = useHistory();
  const {
    state: {
      Props: { itemsCountPerPage },
    },
  } = useContext(mainStore);
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  async function getOrders(
    page = 1,
    limit = itemsCountPerPage,
    driverId,
    destinationState
  ) {
    try {
      setIsLoadingOrder(true);
      const urlParams = new URLSearchParams('');
      urlParams.set('page', page);
      urlParams.set('limit', limit);
      urlParams.set('driverId', driverId);
      destinationState?.forEach((d) => {
        urlParams.append('destinationState', d);
      });
      const { data: orders } = await axios.get(
        `/api/v1/admin/orders/list?${urlParams.toString()}`
      );
      setOrders(orders);
      return orders;
    } catch (error) {
      return new Error(error);
    } finally {
      setIsLoadingOrder(false);
    }
  }

  useEffect(() => {
    if (!params.get('modal')) return;
    getOrders(
      params.get('page') || 1,
      itemsCountPerPage,
      driverId,
      destinationStates?.map((d) => d.value || [])
    );
  }, [location.search, destinationStates, driverId]);

  const columns = useMemo(
    () => [
      {
        field: 'loadingDate',
        headerName: 'بارگیری',
        width: 120,
        renderCell: ({ value }) =>
          moment(value.loadDate).format('jMM/jDD - HH:mm'),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'customer',
        headerName: 'صاحب‌ بار',
        width: 180,
        renderCell: ({ value: { customer } }) => (
          <Avatar user={customer} fullName width={30} height={30} />
        ),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'origin',
        headerName: 'مبدا',
        width: 100,
        renderCell: ({ value: { origin } }) => origin.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'fleetOptions',
        headerName: 'ناوگان',
        flex: 1,
        renderCell: ({ value: { fleetOptions } }) =>
          fleetOptions.map((f) => `${f.fleet.title}(${f.title})`).join('، '),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'fleetCount',
        headerName: 'تعداد ناوگان',
        width: 150,
        renderCell: ({ value: { _id, fleetCount, drivers } }) => {
          if (fleetCount === 1)
            return <Link to={`/protected/admin/orders/${_id}#fleets`}>1</Link>;
          else
            return (
              <Link to={`/protected/admin/orders/${_id}#fleets`}>
                <div className={classes.fleetCount}>
                  <span className="red">
                    مانده:{fleetCount - drivers.length}
                  </span>
                  <span className="green">آماده:{drivers.length}</span>
                </div>
              </Link>
            );
        },
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'destination',
        headerName: 'مقصد',
        width: 100,
        renderCell: ({ value: { destination } }) => destination.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'netDriverRevenue',
        headerName: 'صافی راننده',
        flex: 1,
        renderCell: ({ value: order }) => {
          if (order?.proposedPrice > 0) {
            const result =
              order.proposedPrice * 1 - (order?.totalCommission || 0) * 1;
            return formatNumber(result);
          }
          return '';
        },
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'loadingTime',
        headerName: 'زمان‌بارگیری',
        width: 120,
        renderCell: ({ value: { loadTime } }) => __(loadTime),
        cellClassName: ({ value: { status } }) => `${status} loadTime`,
        headerClassName: 'colHeader',
      },
      {
        field: 'loadType',
        headerName: 'محموله',
        width: 100,
        renderCell: ({ value: { cargos } }) => cargos[0].type.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'packaging',
        headerName: 'بسته‌بندی',
        width: 100,
        renderCell: ({ value: { cargos } }) => cargos?.[0]?.packaging?.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'status',
        headerName: 'وضعیت',
        width: 100,
        renderCell: ({ value: { status } }) => __(status),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
    ],
    [fullScreen]
  );

  const minimizeScreenColumns = useMemo(
    () => [
      {
        field: 'loadingDate',
        headerName: 'بارگیری',
        width: 120,
        renderCell: ({ value }) =>
          moment(value.loadDate).format('jMM/jDD - HH:mm'),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'customer',
        headerName: 'صاحب‌بار',
        width: 180,
        renderCell: ({ value: { customer } }) => (
          <Avatar user={customer} fullName width={30} height={30} />
        ),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'origin',
        headerName: 'مبدا',
        width: 90,
        renderCell: ({ value: { origin } }) => origin.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'fleetOptions',
        headerName: 'ناوگان',
        width: 130,
        renderCell: ({ value: { fleetOptions } }) =>
          fleetOptions.map((f) => `${f.fleet.title}(${f.title})`).join('، '),
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'fleetCount',
        headerName: 'تعداد ناوگان',
        width: 140,
        renderCell: ({ value: { _id, fleetCount, drivers } }) => {
          if (fleetCount === 1)
            return <Link to={`/protected/admin/orders/${_id}#fleets`}>1</Link>;
          else
            return (
              <Link to={`/protected/admin/orders/${_id}#fleets`}>
                <div className={classes.fleetCount}>
                  <span className="red">
                    مانده:{fleetCount - drivers.length}
                  </span>
                  <span className="green">آماده:{drivers.length}</span>
                </div>
              </Link>
            );
        },
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'destination',
        headerName: 'مقصد',
        width: 100,
        renderCell: ({ value: { destination } }) => destination.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
      {
        field: 'loadType',
        headerName: 'محموله',
        width: 130,
        renderCell: ({ value: { cargos } }) => cargos[0].type.title,
        cellClassName: ({ value: { status } }) => status,
        headerClassName: 'colHeader',
      },
    ],
    [fullScreen]
  );

  const handlePageChange = async (page) => {
    setParams([{ name: 'page', value: page }]);
    setPage(page);
  };
  const handleSelectOrder = ({ row, field }) => {
    const { id } = row;
    const fields = ['customer', 'fleetCount', 'operator'];
    if (fields.includes(field)) return;
    history.push(`/protected/admin/orders/${id}`);
  };

  return (
    <div className={classes.root}>
      <DataGrid
        rows={
          orders?.docs?.map((order) => ({
            id: order._id,
            loadingDate: order,
            customer: order,
            origin: order,
            fleetOptions: order,
            fleetCount: order,
            destination: order,
            netDriverRevenue: order,
            loadingTime: order,
            loadType: order,
            packaging: order,
            status: order,
            operator: order,
          })) || []
        }
        columns={fullScreen ? columns : minimizeScreenColumns}
        localeText={localText}
        pageSize={Number(itemsCountPerPage)}
        rowCount={orders && orders.totalDocs ? orders.totalDocs : 0}
        paginationMode="server"
        onPageChange={handlePageChange}
        loading={isLoadingOrder}
        footerPagination={true}
        totalPages={orders?.totalPages}
        defaultPage={orders?.page}
        pageNumber={params.get('page') * 1 || 1}
        disableMultipleSelection
        onCellClick={handleSelectOrder}
        density="compact"
        // onSelectionChange={handleSelectOrder}
      />
    </div>
  );
};

export default React.memo(OrderListInterestesDrivers);
